import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Portrait } from "@/components/site/Owner";
import { Eyebrow, Lines, Reveal } from "@/components/site/Motion";
import { useGo } from "@/lib/nav";

const TIMELINE = [
  { k: "Early years", t: "Learning on the job site", d: "Working alongside my dad in his construction business. That's where it started." },
  { k: "College", t: "Construction Management", d: "Earned an undergraduate degree in Construction Management." },
  { k: "Graduate", t: "Business Technology", d: "Went on to complete a Master's in Business Technology." },
  { k: "15 years", t: "ETSU Facilities Management", d: "Fifteen years in Facilities Management at East Tennessee State University." },
  { k: "2022", t: "Newland Construction", d: "Founded Newland Construction to follow my passion for residential construction." },
];

const Timeline = () => (
  <div className="relative">
    <div className="absolute bottom-0 left-[1.1rem] top-0 w-px bg-white/10" />
    <div className="space-y-14">
      {TIMELINE.map((s, i) => (
        <Reveal key={s.t} className="relative pl-16">
          <span data-testid={`about-timeline-${i}`} className={`absolute left-0 top-0 flex h-9 w-9 items-center justify-center rounded-full border font-mono text-xs ${i === TIMELINE.length - 1 ? "border-white bg-white text-[#0A0A0A]" : "border-white/20 bg-[#0A0A0A] text-neutral-300"}`}>{`0${i + 1}`}</span>
          <div className="font-mono text-[0.65rem] uppercase tracking-[0.25em] text-neutral-500">{s.k}</div>
          <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-white md:text-3xl">{s.t}</h3>
          <p className="mt-2 max-w-md text-neutral-400">{s.d}</p>
        </Reveal>
      ))}
    </div>
  </div>
);

export default function About() {
  const go = useGo();
  return (
    <main data-testid="about-page" className="bg-[#0A0A0A]">
      <section className="relative overflow-hidden px-5 pb-24 pt-36 md:px-10 md:pb-32 md:pt-44">
        <div className="mx-auto grid max-w-[1500px] items-end gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Eyebrow>About the owner</Eyebrow>
            <h1 data-testid="about-title" className="mt-8 font-display text-6xl font-extrabold leading-[0.9] tracking-tighter text-white sm:text-7xl lg:text-[8.5rem]">
              <Lines trigger delay={0.2} lines={["Matt", <span key="n" className="font-serif font-normal italic text-neutral-400">Newland.</span>]} />
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-10 max-w-xl font-serif text-2xl leading-snug text-neutral-200 md:text-3xl">
                "My name is Matt Newland. I'm the owner of Newland Construction."
              </p>
            </Reveal>
          </div>
          <div className="corner-ticks lg:col-span-5"><Portrait className="aspect-[4/5]" /></div>
        </div>
      </section>

      <section className="border-t border-white/[0.08] bg-[#F2F1ED] px-5 py-28 text-[#0A0A0A] md:px-10 md:py-40">
        <div className="mx-auto grid max-w-[1500px] gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Eyebrow className="!text-[#0A0A0A]/60">In his words</Eyebrow>
          </div>
          <div data-testid="about-bio" className="space-y-8 lg:col-span-8">
            <Reveal>
              <p className="font-display text-3xl font-bold leading-[1.15] tracking-tight md:text-5xl">
                My knowledge of construction started when I was just a kid, working with my dad for his construction business.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-2xl text-lg leading-relaxed text-[#0A0A0A]/70">
                In college, I earned my undergraduate degree in Construction Management and a Master's in Business Technology. I worked at ETSU for 15 years in Facilities Management.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="max-w-2xl text-lg leading-relaxed text-[#0A0A0A]/70">
                I decided to pursue my passion for residential construction and start my own company. Please reach out for a free estimate on your next project!
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="font-serif text-4xl italic">Matt Newland</div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-5 py-28 md:px-10 md:py-40">
        <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Eyebrow>The path here</Eyebrow>
              <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tighter text-white md:text-6xl">
                <Lines lines={["A lifetime", <span key="p" className="font-serif font-normal italic text-neutral-400">around buildings.</span>]} />
              </h2>
            </div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7"><Timeline /></div>
        </div>
      </section>

      <section className="border-t border-white/[0.08] px-5 py-28 md:px-10 md:py-36">
        <div className="mx-auto flex max-w-[1500px] flex-col items-start justify-between gap-10 md:flex-row md:items-end">
          <h2 className="font-display text-5xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-7xl">
            Let's talk about<br /><span className="font-serif font-normal italic text-neutral-400">your next project.</span>
          </h2>
          <div className="flex flex-wrap gap-3">
            <button data-testid="about-estimate-button" onClick={() => go("quote")}
              className="group flex items-center gap-3 rounded-full bg-white py-4 pl-7 pr-5 font-semibold text-[#0A0A0A] transition-colors hover:bg-neutral-200">
              Get a free estimate
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A0A0A] text-white transition-transform duration-500 group-hover:rotate-45"><ArrowUpRight className="h-4 w-4" /></span>
            </button>
            <Link to="/" data-testid="about-home-link" className="rounded-full border border-white/15 px-7 py-4 font-medium text-neutral-100 transition-colors hover:border-white/60">See our work</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
